import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  RemoveFormatting,
  Underline,
} from "lucide-react";
import { cn } from "@/lib/cn";
import {
  autolinkUrlBeforeCaret,
  insertAutolinkedPlainText,
  isAutolinkSkipNode,
  isStandaloneAutolinkText,
  linkifyPlainTextToHtml,
} from "@/lib/compose/composeAutolink";
import { htmlToPlainText } from "@/lib/compose/composeHtml";
import { dataTransferHasFiles, filesFromDataTransfer } from "@/lib/compose/composeAttachments";
import { openUrlInSystemBrowser } from "@/lib/mail/openExternalUrl";
import {
  applyEditorLink,
  applyFontFamily,
  applyFontSize,
  cloneSelectionStore,
  DEFAULT_RICH_TOOLBAR_STATE,
  EMPTY_SELECTION,
  execRichCommand,
  focusEditorAndRestoreSelection,
  FONT_FACES,
  FONT_SIZES,
  initRichEditorParagraphMode,
  readSelectionToolbarState,
  saveEditorSelection,
  type FontFaceId,
  type FontSizeId,
  type RichToolbarState,
  type SelectionStore,
} from "@/lib/compose/richTextFormat";
import { ComposeEmojiModal, ComposeEmojiToggle } from "@/features/compose/ComposeEmojiPicker";
import { ComposeLinkModal } from "@/features/compose/ComposeLinkModal";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeightClass?: string;
  fillHeight?: boolean;
  className?: string;
  ariaLabel?: string;
  /** Drop fichiers → PJ (évite l’insertion image dans le HTML). */
  onFilesDropped?: (files: File[]) => void | Promise<void>;
};

type FormatButton = {
  id: keyof Pick<RichToolbarState, "bold" | "italic" | "underline" | "unorderedList" | "orderedList">;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  command: string;
};

const FORMAT_BUTTONS: FormatButton[] = [
  { id: "bold", label: "Gras", icon: Bold, command: "bold" },
  { id: "italic", label: "Italique", icon: Italic, command: "italic" },
  { id: "underline", label: "Souligné", icon: Underline, command: "underline" },
  { id: "unorderedList", label: "Liste à puces", icon: List, command: "insertUnorderedList" },
  { id: "orderedList", label: "Liste numérotée", icon: ListOrdered, command: "insertOrderedList" },
];

const TOOLBAR_BTN =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]";

function toolbarBtnActive(active: boolean) {
  return cn(
    TOOLBAR_BTN,
    active && "bg-[var(--mail-surface-hover)] text-[var(--mail-text)] ring-1 ring-[var(--mail-accent)]/25"
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Écrivez votre message…",
  minHeightClass = "min-h-[12rem]",
  fillHeight = false,
  className,
  ariaLabel = "Éditeur de message",
  onFilesDropped,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const selectionStore = useRef<SelectionStore>({ ...EMPTY_SELECTION });
  const linkSelectionStore = useRef<SelectionStore>({ ...EMPTY_SELECTION });
  const paragraphModeReady = useRef(false);
  const overlayOpenRef = useRef(false);
  const lastEmitted = useRef<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [toolbarState, setToolbarState] = useState<RichToolbarState>(DEFAULT_RICH_TOOLBAR_STATE);

  overlayOpenRef.current = emojiOpen || linkOpen;

  const emitChange = useCallback(() => {
    const html = editorRef.current?.innerHTML ?? "";
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const refreshToolbar = useCallback(() => {
    if (!focused && !overlayOpenRef.current) return;
    const next = readSelectionToolbarState(editorRef.current);
    setToolbarState((prev) => {
      const same = (Object.keys(next) as (keyof RichToolbarState)[]).every(
        (key) => prev[key] === next[key]
      );
      return same ? prev : next;
    });
  }, [focused]);

  const refocusEditor = useCallback(() => {
    focusEditorAndRestoreSelection(editorRef.current, selectionStore.current);
    setFocused(true);
  }, []);

  const runFormat = useCallback(
    (command: string, commandValue?: string) => {
      execRichCommand(editorRef.current, selectionStore.current, command, commandValue);
      emitChange();
      requestAnimationFrame(refreshToolbar);
    },
    [emitChange, refreshToolbar]
  );

  const handleToolbarPointerDown = useCallback(() => {
    saveEditorSelection(editorRef.current, selectionStore.current);
  }, []);

  const handleEditorBlur = useCallback(() => {
    window.setTimeout(() => {
      if (overlayOpenRef.current) return;
      const active = document.activeElement;
      if (active instanceof Node && rootRef.current?.contains(active)) return;
      if (autolinkUrlBeforeCaret(editorRef.current)) {
        saveEditorSelection(editorRef.current, selectionStore.current);
      }
      setFocused(false);
      emitChange();
    }, 0);
  }, [emitChange]);

  const handleEditorPaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      if (onFilesDropped && dataTransferHasFiles(event.clipboardData)) return;

      const text = event.clipboardData.getData("text/plain");
      if (!text.trim()) return;

      const sel = window.getSelection();
      if (sel?.anchorNode && isAutolinkSkipNode(sel.anchorNode)) return;

      const html = event.clipboardData.getData("text/html");
      const htmlHasLink = /<a\s[^>]*href/i.test(html);
      if (htmlHasLink && !isStandaloneAutolinkText(text)) return;
      if (!linkifyPlainTextToHtml(text).changed) return;

      event.preventDefault();
      insertAutolinkedPlainText(text);
      saveEditorSelection(editorRef.current, selectionStore.current);
      emitChange();
      requestAnimationFrame(refreshToolbar);
    },
    [emitChange, onFilesDropped, refreshToolbar]
  );

  const handleEditorKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || event.nativeEvent.isComposing || event.key === "Process") return;
      if (event.key !== " " && event.key !== "Enter") return;
      if (autolinkUrlBeforeCaret(editorRef.current)) {
        saveEditorSelection(editorRef.current, selectionStore.current);
        emitChange();
      }
    },
    [emitChange]
  );

  const handleEditorClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest("a[href]");
    if (!link || !editorRef.current?.contains(link)) return;
    event.preventDefault();
    if (!event.metaKey && !event.ctrlKey) return;
    const href = link.getAttribute("href");
    if (href) openUrlInSystemBrowser(href);
  }, []);

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || focused || overlayOpenRef.current) return;

    const next = value || "";
    if (el.innerHTML === next) {
      lastEmitted.current = next;
      return;
    }

    el.innerHTML = next;
    lastEmitted.current = next;
  }, [focused, value, linkOpen, emojiOpen]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || focused || overlayOpenRef.current) return;
    if (value !== lastEmitted.current && el.innerHTML !== value) {
      el.innerHTML = value || "";
      lastEmitted.current = value;
    }
  }, [focused, value, linkOpen, emojiOpen]);

  useEffect(() => {
    if (!emojiOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        overlayOpenRef.current = false;
        setEmojiOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        overlayOpenRef.current = false;
        setEmojiOpen(false);
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [emojiOpen]);

  useEffect(() => {
    if (!focused && !overlayOpenRef.current) return;
    refreshToolbar();
    document.addEventListener("selectionchange", refreshToolbar);
    return () => document.removeEventListener("selectionchange", refreshToolbar);
  }, [focused, linkOpen, emojiOpen, refreshToolbar]);

  function closeLinkModal() {
    overlayOpenRef.current = false;
    setLinkOpen(false);
    window.requestAnimationFrame(() => refocusEditor());
  }

  function handleConfirmLink(url: string) {
    const snapshot = cloneSelectionStore(linkSelectionStore.current);
    overlayOpenRef.current = false;
    setLinkOpen(false);
    window.requestAnimationFrame(() => {
      applyEditorLink(editorRef.current, snapshot, url);
      saveEditorSelection(editorRef.current, selectionStore.current);
      setFocused(true);
      emitChange();
      requestAnimationFrame(refreshToolbar);
    });
  }

  function handleClearFormat() {
    runFormat("removeFormat");
    runFormat("unlink");
  }

  function insertEmoji(emoji: string) {
    refocusEditor();
    document.execCommand("insertText", false, emoji);
    saveEditorSelection(editorRef.current, selectionStore.current);
    emitChange();
  }

  function handleFontChange(next: FontFaceId) {
    const face = FONT_FACES.find((entry) => entry.id === next);
    if (!face) return;
    window.requestAnimationFrame(() => {
      refocusEditor();
      applyFontFamily(editorRef.current, selectionStore.current, face);
      emitChange();
      requestAnimationFrame(refreshToolbar);
    });
  }

  function handleSizeChange(next: FontSizeId) {
    const size = FONT_SIZES.find((entry) => entry.id === next);
    if (!size) return;
    window.requestAnimationFrame(() => {
      refocusEditor();
      applyFontSize(editorRef.current, selectionStore.current, size);
      emitChange();
      requestAnimationFrame(refreshToolbar);
    });
  }

  const isEmpty = !value.trim() || htmlToPlainText(value).length === 0;

  return (
    <div
      ref={rootRef}
      className={cn(
        "mail-rich-editor overflow-hidden rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] shadow-[var(--mail-shadow-sm)]",
        fillHeight && "flex min-h-0 flex-col",
        focused && "border-[var(--mail-accent)]/40 ring-2 ring-[var(--mail-accent)]/15",
        className
      )}
    >
      <div className="mail-rich-editor__toolbar flex flex-wrap items-center gap-0.5 border-b border-[var(--mail-border-soft)] bg-[var(--mail-bg)]/40 px-1.5 py-1">
        <label className="sr-only" htmlFor="mail-compose-font">
          Police d’écriture
        </label>
        <select
          id="mail-compose-font"
          value={toolbarState.font}
          onPointerDown={handleToolbarPointerDown}
          onChange={(e) => handleFontChange(e.target.value as FontFaceId)}
          className="mail-rich-editor__select mail-rich-editor__select--font h-7"
          aria-label="Police d’écriture"
        >
          {FONT_FACES.map((face) => (
            <option key={face.id} value={face.id} style={{ fontFamily: face.stack }}>
              {face.label}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="mail-compose-font-size">
          Taille de police
        </label>
        <select
          id="mail-compose-font-size"
          value={toolbarState.size}
          onPointerDown={handleToolbarPointerDown}
          onChange={(e) => handleSizeChange(e.target.value as FontSizeId)}
          className="mail-rich-editor__select h-7 w-[4.25rem]"
          aria-label="Taille de police"
        >
          {FONT_SIZES.map((size) => (
            <option key={size.id} value={size.id}>
              {size.px}px
            </option>
          ))}
        </select>

        <span className="mx-1 h-5 w-px bg-[var(--mail-border-soft)]" aria-hidden />

        <button
          type="button"
          title="Aligner à gauche"
          aria-label="Aligner à gauche"
          aria-pressed={toolbarState.align === "left"}
          onPointerDown={handleToolbarPointerDown}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runFormat("justifyLeft")}
          className={toolbarBtnActive(toolbarState.align === "left")}
        >
          <AlignLeft className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          title="Centrer"
          aria-label="Centrer"
          aria-pressed={toolbarState.align === "center"}
          onPointerDown={handleToolbarPointerDown}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runFormat("justifyCenter")}
          className={toolbarBtnActive(toolbarState.align === "center")}
        >
          <AlignCenter className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          title="Aligner à droite"
          aria-label="Aligner à droite"
          aria-pressed={toolbarState.align === "right"}
          onPointerDown={handleToolbarPointerDown}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => runFormat("justifyRight")}
          className={toolbarBtnActive(toolbarState.align === "right")}
        >
          <AlignRight className="h-3.5 w-3.5" aria-hidden />
        </button>

        <span className="mx-1 h-5 w-px bg-[var(--mail-border-soft)]" aria-hidden />

        {FORMAT_BUTTONS.map((btn) => {
          const Icon = btn.icon;
          const active = toolbarState[btn.id];
          return (
            <button
              key={btn.id}
              type="button"
              title={btn.label}
              aria-label={btn.label}
              aria-pressed={active}
              onPointerDown={handleToolbarPointerDown}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runFormat(btn.command)}
              className={toolbarBtnActive(active)}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </button>
          );
        })}

        <button
          type="button"
          title="Insérer un lien"
          aria-label="Insérer un lien"
          onMouseDown={(e) => {
            e.preventDefault();
            saveEditorSelection(editorRef.current, selectionStore.current);
            linkSelectionStore.current = cloneSelectionStore(selectionStore.current);
            overlayOpenRef.current = true;
            setLinkOpen(true);
          }}
          className={TOOLBAR_BTN}
        >
          <Link2 className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          title="Effacer la mise en forme"
          aria-label="Effacer la mise en forme"
          onPointerDown={handleToolbarPointerDown}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClearFormat}
          className={TOOLBAR_BTN}
        >
          <RemoveFormatting className="h-3.5 w-3.5" aria-hidden />
        </button>

        <span className="ml-auto">
          <ComposeEmojiToggle
            open={emojiOpen}
            onToggle={() => {
              saveEditorSelection(editorRef.current, selectionStore.current);
              setEmojiOpen((open) => {
                const next = !open;
                overlayOpenRef.current = next || linkOpen;
                return next;
              });
            }}
          />
        </span>
      </div>

      <div
        className={cn(
          "mail-rich-editor__body-wrap relative",
          fillHeight && "flex min-h-0 flex-1 flex-col"
        )}
      >
        {emojiOpen ? (
          <>
            <button
              type="button"
              aria-label="Fermer la bibliothèque d’emojis"
              className="mail-compose-emoji-backdrop"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                overlayOpenRef.current = false;
                setEmojiOpen(false);
                refocusEditor();
              }}
            />
            <ComposeEmojiModal
              onPick={insertEmoji}
              onClose={() => {
                overlayOpenRef.current = false;
                setEmojiOpen(false);
                refocusEditor();
              }}
            />
          </>
        ) : null}

        <div
          ref={editorRef}
          contentEditable
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="true"
          data-placeholder={placeholder}
          data-empty={isEmpty ? "true" : "false"}
          onInput={() => {
            saveEditorSelection(editorRef.current, selectionStore.current);
            emitChange();
            refreshToolbar();
          }}
          onPaste={handleEditorPaste}
          onKeyDown={handleEditorKeyDown}
          onClick={handleEditorClick}
          onMouseUp={() => {
            saveEditorSelection(editorRef.current, selectionStore.current);
            refreshToolbar();
          }}
          onKeyUp={() => {
            saveEditorSelection(editorRef.current, selectionStore.current);
            refreshToolbar();
          }}
          onBlur={handleEditorBlur}
          onFocus={() => {
            if (!paragraphModeReady.current && editorRef.current) {
              initRichEditorParagraphMode(editorRef.current);
              paragraphModeReady.current = true;
            }
            setFocused(true);
            saveEditorSelection(editorRef.current, selectionStore.current);
            requestAnimationFrame(refreshToolbar);
          }}
          onDragOver={(event) => {
            if (!onFilesDropped || !dataTransferHasFiles(event.dataTransfer)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(event) => {
            if (!onFilesDropped || !dataTransferHasFiles(event.dataTransfer)) return;
            event.preventDefault();
            event.stopPropagation();
            const files = filesFromDataTransfer(event.dataTransfer);
            if (files.length > 0) void onFilesDropped(files);
          }}
          className={cn(
            "mail-rich-editor__body relative z-0 px-3 py-2 text-sm leading-relaxed text-[var(--mail-text)] outline-none",
            fillHeight ? "min-h-0 flex-1 overflow-y-auto" : minHeightClass
          )}
          suppressContentEditableWarning
        />
      </div>

      <ComposeLinkModal
        open={linkOpen}
        onClose={closeLinkModal}
        onConfirm={handleConfirmLink}
      />
    </div>
  );
}
